import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import * as XLSX from 'xlsx';

import styles from './XlsxRenderer.module.css';
import ContentHeader from '../ContentHeader';

const XlsxRenderer = ({ contentDetails, viewerUrl }) => {
  const [workbook, setWorkbook] = useState(null);
  const [activeSheet, setActiveSheet] = useState(null);
  const [sheetHtml, setSheetHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const tableContainerRef = useRef(null);

  /*
   * Load XLSX from your existing authenticated backend endpoint.
   */
  useEffect(() => {
    let cancelled = false;

    const loadWorkbook = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(viewerUrl, {});

        if (!response.ok) {
          throw new Error(`Failed to load spreadsheet (${response.status})`);
        }

        /*
         * SheetJS read() accepts ArrayBuffer directly.
         */
        const arrayBuffer = await response.arrayBuffer();

        if (cancelled) return;

        const wb = XLSX.read(arrayBuffer, {
          cellDates: true,
          dense: true,
        });

        if (!wb.SheetNames.length) {
          throw new Error('The workbook does not contain any sheets.');
        }

        setWorkbook(wb);
        setActiveSheet(wb.SheetNames[0]);
      } catch (err) {
        console.error('Failed to load XLSX:', err);

        if (!cancelled) {
          setError(err?.message || 'Unable to load the spreadsheet.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    if (viewerUrl) {
      loadWorkbook();
    }

    return () => {
      cancelled = true;
    };
  }, [viewerUrl]);

  /*
   * Convert active worksheet to HTML.
   */
  useEffect(() => {
    if (!workbook || !activeSheet) {
      setSheetHtml('');
      return;
    }

    const worksheet = workbook.Sheets[activeSheet];

    if (!worksheet) {
      setSheetHtml('');
      return;
    }

    const html = XLSX.utils.sheet_to_html(worksheet, {
      id: 'xlsx-sheet',
      editable: false,
    });

    setSheetHtml(html);
  }, [workbook, activeSheet]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading spreadsheet...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <div className={styles.errorTitle}>Unable to load spreadsheet</div>

          <div className={styles.errorMessage}>{error}</div>
        </div>
      </div>
    );
  }

  if (!workbook) {
    return null;
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      {/* <motion.div
        className={styles.header}
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: 0.25,
          ease: [0.16, 1, 0.3, 1],
        }}
      >
        <div className={styles.title}>
          {contentDetails?.metadata?.file_metadata?.name ||
            contentDetails?.file_name ||
            'Document'}
        </div>
      </motion.div>*/}

      <ContentHeader
        title={
          contentDetails?.title ||
          contentDetails?.metadata?.file_metadata?.name ||
          contentDetails?.file_name ||
          'Document'
        }
        url={viewerUrl}
        config={contentDetails?.metadata?.configurations || {}}
      />

      {/* Spreadsheet */}
      <div
        className={styles.sheetWrapper}
      >
        <div
          ref={tableContainerRef}
          className={styles.tableContainer}
          dangerouslySetInnerHTML={{
            __html: sheetHtml,
          }}
        />
      </div>

      {/* Sheet tabs */}
      <div className={styles.sheetTabs}>
        {workbook.SheetNames.map((sheetName) => (
          <button
            key={sheetName}
            type="button"
            className={`${styles.sheetTab} ${
              activeSheet === sheetName ? styles.activeSheetTab : ''
            }`}
            onClick={() => setActiveSheet(sheetName)}
          >
            {sheetName}
          </button>
        ))}
      </div>
    </div>
  );
};

export default XlsxRenderer;

